import React, { useState } from 'react';
import { Mail, Phone, Send, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus({ type: 'success', message: data.message });
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setStatus({ 
          type: 'error', 
          message: data.message || 'Something went wrong. Please try again.' 
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setStatus({ 
        type: 'error', 
        message: 'Unable to send message. Please try again or contact us directly.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <section className="section-padding-small">
        <div className="container">
          <h1 className="hero-large" style={{ marginBottom: '24px' }}>
            Let's Talk.
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px' }}>
            If you're ready to shift something — this is where it starts. You don't need the perfect words. Just reach out.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section style={{ paddingBottom: '120px' }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '80px',
            alignItems: 'start'
          }} className="responsive-grid">
            {/* Contact Form */}
            <div>
              <h2 className="heading-2" style={{ marginBottom: '24px' }}>
                Start Here
              </h2>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
                The best first step is a 20-minute Discovery Call (online). We'll clarify what you want to change and whether this work is the right fit for you. Clear. Calm. No pressure.
              </p>
              
              {status.message && (
                <div style={{
                  padding: '16px',
                  background: status.type === 'success' ? 'rgba(160, 216, 210, 0.2)' : 'rgba(220, 53, 69, 0.1)',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px'
                }} data-testid="form-status">
                  {status.type === 'success' ? (
                    <CheckCircle size={20} style={{ color: '#2a9d8f', flexShrink: 0, marginTop: '2px' }} />
                  ) : (
                    <AlertCircle size={20} style={{ color: '#dc3545', flexShrink: 0, marginTop: '2px' }} />
                  )}
                  <span className="body-regular" style={{ 
                    color: status.type === 'success' ? '#2a9d8f' : '#dc3545' 
                  }}>
                    {status.message}
                  </span>
                </div>
              )}

              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="name" style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: 'var(--text-primary)'
                  }}>
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="contact-name-input"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '1px solid var(--border-medium)',
                      fontSize: '14px',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="email" style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: 'var(--text-primary)'
                  }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="contact-email-input"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '1px solid var(--border-medium)',
                      fontSize: '14px',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="phone" style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: 'var(--text-primary)'
                  }}>
                    Phone Number (optional)
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    data-testid="contact-phone-input"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '1px solid var(--border-medium)',
                      fontSize: '14px',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="message" style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: 'var(--text-primary)'
                  }}>
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    data-testid="contact-message-input"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '1px solid var(--border-medium)',
                      fontSize: '14px',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit-button"
                  style={{
                    background: isSubmitting ? 'var(--bg-secondary)' : 'transparent',
                    color: isSubmitting ? 'var(--text-secondary)' : 'var(--interactive-base)',
                    border: '1px solid var(--interactive-base)',
                    padding: '19px 23px',
                    minWidth: '200px',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    opacity: isSubmitting ? 0.7 : 1
                  }}
                  className="btn-primary"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'} 
                  {!isSubmitting && <Send size={16} />}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="heading-2" style={{ marginBottom: '32px' }}>
                Direct Contact
              </h2>

              <div style={{ marginBottom: '48px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '16px',
                  marginBottom: '24px'
                }}>
                  <Mail size={20} style={{ color: 'var(--text-primary)', marginTop: '2px' }} />
                  <div>
                    <a href="mailto:getlucidwithtaraphillips@gmail.com" style={{
                      color: 'var(--text-secondary)',
                      textDecoration: 'none',
                      fontSize: '14px'
                    }}>
                      getlucidwithtaraphillips@gmail.com
                    </a>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '16px'
                }}>
                  <Phone size={20} style={{ color: 'var(--text-primary)', marginTop: '2px' }} />
                  <div>
                    <a href="tel:+919008455435" style={{
                      color: 'var(--text-secondary)',
                      textDecoration: 'none',
                      fontSize: '14px'
                    }}>
                      +91 9008 455 435
                    </a>
                  </div>
                </div>
              </div>

              <div style={{
                padding: '32px',
                background: 'var(--bg-secondary)'
              }}>
                <h3 className="heading-3" style={{ marginBottom: '16px' }}>
                  Office Timings
                </h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  Tuesday – Saturday: 10am – 7pm
                </p>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  Based in Bangalore. Working globally.
                </p>
              </div>

              <p className="body-regular" style={{ color: 'var(--text-secondary)', marginTop: '32px', fontStyle: 'italic' }}>
                If something inside you says "it's time" — trust that.
              </p>
              <p className="body-regular" style={{ color: 'var(--text-primary)', marginTop: '8px' }}>
                — Tara
              </p>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .btn-primary:hover:not(:disabled) {
          background: var(--interactive-base) !important;
          color: var(--bg-primary) !important;
        }
        input:focus,
        textarea:focus {
          outline: 2px solid var(--focus-outline);
          outline-offset: 2px;
        }
        @media (max-width: 1024px) {
          .responsive-grid {
            grid-template-columns: 1fr !important;
            gap: 48px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ContactPage;
